import PropTypes from "prop-types";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import MDBadge from "components/MDBadge";

// === Status mapping για 0/1/2 (numeric) ===
export const STATUS_LABELS = {
  0: "ΕΝΕΡΓΗ",
  1: "ΛΗΓΕΙ ΣΥΝΤΟΜΑ",
  2: "ΛΗΓΜΕΝΗ",
};
export const STATUS_COLORS = {
  0: "success",
  1: "warning",
  2: "error",
};

// Υπολογισμός ημερών ως σήμερα μέχρι endISO
export function daysUntil(endISO) {
  if (!endISO) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endISO);
  end.setHours(0, 0, 0, 0);
  const diffMs = end.getTime() - today.getTime();
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.ceil(diffMs / MS_PER_DAY);
}

// Tooltip ανά status & daysLeft
export function formatStatusTip(code, daysLeft) {
  const label = STATUS_LABELS[code] ?? String(code);
  if (typeof daysLeft !== "number" || Number.isNaN(daysLeft)) return label;

  if (code === 2) {
    const d = Math.abs(daysLeft);
    return `Έληξε ${d} ${d === 1 ? "μέρα" : "μέρες"} πριν`;
  }
  if (code === 1) {
    return `Απομένουν ${daysLeft} ${daysLeft === 1 ? "μέρα" : "μέρες"}`;
  }
  // code === 0
  return daysLeft >= 0
    ? `Απομένουν ~${daysLeft} ${daysLeft === 1 ? "μέρα" : "μέρες"}`
    : `Έληξε ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? "μέρα" : "μέρες"} πριν`;
}

// === ServiceStatusBadge: ένα main badge + μικρό bubble με τις ημέρες (σε όλα τα status) ===
export default function ServiceStatusBadge({ statusCode, daysLeft, endDate }) {
  const code = Number.isNaN(Number(statusCode)) ? null : Number(statusCode);
  const label = STATUS_LABELS[code] ?? String(statusCode);
  const color = STATUS_COLORS[code] ?? "info";

  // Αν δεν δώσεις daysLeft, το βγάζει από endDate
  const effectiveDaysLeft =
    typeof daysLeft === "number"
      ? daysLeft
      : typeof endDate === "string"
      ? daysUntil(endDate)
      : null;

  const tip = formatStatusTip(code, effectiveDaysLeft);

  const hasNumber = typeof effectiveDaysLeft === "number" && !Number.isNaN(effectiveDaysLeft);
  const bubbleValue = hasNumber ? Math.abs(effectiveDaysLeft) : null;
  const bubbleColor = STATUS_COLORS[code] ?? "info";

  return (
    <Badge
      overlap="rectangular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      color={bubbleColor}
      invisible={!hasNumber}
      badgeContent={bubbleValue}
      max={100}
      showZero
      sx={{
        "& > .MuiBadge-badge": {
          fontSize: "0.55rem",
          height: 16,
          minWidth: 16,
          px: 0.5,
          border: "1px solid rgba(122, 122, 122, 0.85)",
          boxShadow: 1,
          fontWeight: 600,
        },
      }}
    >
      <Tooltip title={tip} arrow>
        <span>
          <MDBadge badgeContent={label} color={color} variant="gradient" size="sm" />
        </span>
      </Tooltip>
    </Badge>
  );
}

ServiceStatusBadge.propTypes = {
  statusCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  daysLeft: PropTypes.number, // προαιρετικό: αν λείπει, θα χρησιμοποιηθεί το endDate
  endDate: PropTypes.string, // προαιρετικό: ISO ημερομηνία λήξης (yyyy-mm-dd)
};
